import { resolveDeparture, type ResolvedDeparture } from "@shared/departure";
import { resolveGroupHosts } from "@/lib/eventGroups";
import type { HostProfile } from "@/hooks/useHostProfiles";

/** Columns a booked ticket needs from its departure wave (no profile embeds — hosts come from public_host_profiles). */
export const BOOKED_GROUP_SELECT =
  "event_groups!tickets_group_id_fkey (id, name, start_time, end_time, meeting_venue, whatsapp_group, host_id, secondary_host_id)";

export interface BookedGroup {
  id: string;
  name: string | null;
  start_time: string | null;
  end_time: string | null;
  meeting_venue: string | null;
  whatsapp_group: string | null;
  host_id: string | null;
  secondary_host_id: string | null;
}

export interface BookedEvent {
  title?: string | null;
  start_time: string;
  end_time?: string | null;
  venue?: string | null;
  whatsapp_group?: string | null;
  host_id?: string | null;
  secondary_host_id?: string | null;
}

export interface BookedDepartureView {
  groupId: string | null;
  resolved: ResolvedDeparture;
  hosts: HostProfile[];
}

/** Time/venue/chat/name from the shared resolver; hosts from the shared wave-host rule. */
export const toBookedDeparture = (
  event: BookedEvent,
  group: BookedGroup | null | undefined,
  hostMap: Record<string, HostProfile> | undefined,
): BookedDepartureView => {
  const map = hostMap ?? {};
  const resolved = resolveDeparture(
    { start_time: event.start_time, end_time: event.end_time, venue: event.venue, whatsapp_group: event.whatsapp_group },
    group ?? null,
  );
  const hosts = group
    ? resolveGroupHosts(
        {
          host_id: group.host_id,
          secondary_host_id: group.secondary_host_id,
          host: group.host_id ? map[group.host_id] : undefined,
          secondary_host: group.secondary_host_id ? map[group.secondary_host_id] : undefined,
        },
        {
          primary: event.host_id ? map[event.host_id] : null,
          secondary: event.secondary_host_id ? map[event.secondary_host_id] : null,
        },
      )
    : [];
  return { groupId: group?.id ?? null, resolved, hosts };
};

export const bookedHostIds = (event: BookedEvent | null | undefined, groups: (BookedGroup | null | undefined)[]) => [
  event?.host_id,
  event?.secondary_host_id,
  ...groups.flatMap((g) => [g?.host_id, g?.secondary_host_id]),
];
